import {computed, Injectable, Injector, Signal, signal} from '@angular/core';
import {Organization, Policy, Query as RepositoryQuery} from '../types-repository';
import {Airline, Query as WorkflowQuery} from '../types-workflow';
import {ActivatedRouteSnapshot, NavigationEnd, NavigationSkipped, Router} from '@angular/router';
import {Apollo} from 'apollo-angular';
import {GET_POLICIES} from '../queries-repository';
import {GET_AIRLINES} from '../queries-workflow';
import {HttpHeaders} from '@angular/common/http';
import {distinctUntilChanged, filter, map, tap} from 'rxjs/operators';
import {combineLatest, switchMap} from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class ContextService {
  public organizationId = signal<string | null>(null);
  public organization = signal<Organization | null>(null);
  public airlines = signal<Airline[]>([]);
  public airline = signal<Airline | null>(null);
  public policies = signal<Policy[]>(null);
  public licensedProducts = signal<string[]>(null);

  /**
   * Whether the principal may browse files at all.
   *
   * A content supplier (see policies_displayinteractive_csp.json) delivers into PO directories
   * spread across airlines it holds no grant for, so it legitimately has *zero* airlines. The
   * airline list therefore cannot be the sole gate for UI access -- file-browsing access is on its
   * own a valid reason to be here. Null-safe because `policies` is null until the first query lands.
   */
  public canBrowseFiles = computed(() =>
    (this.policies() ?? []).some(policy =>
      policy.statements?.some(statement =>
        statement.actions?.includes('contentplatform:repository:Query/Folder')
      )
    )
  );

  /**
   * The airline to work in when there is no choice to make.
   *
   * An organization that holds exactly one airline has nothing to select, so the pages that would
   * ask (workflows, previews) navigate straight into it instead. Null while the airline list is
   * still loading, and for every organization that really does offer a choice.
   */
  public soleAirline = computed<Airline | null>(() => {
    const airlines = this.airlines();
    return airlines?.length === 1 ? airlines[0] : null;
  });

  public isAllowed = (action: string, resource: string): Signal<boolean> => {
    return computed(() => {
      const policies = this.policies();

      return policies.some(policy =>
        policy.statements.some(statement => {
          if (!statement.actions.includes(action)) {
            return false;
          }

          if (resource === null) {
            return true;
          }

          const isResourceAllowed = statement.resources.some(statementResource =>
            this.fnMatch(statementResource, resource)
          );

          if (isResourceAllowed && statement.conditions && statement.conditions.length > 0) {
            console.log('Conditions not implemented yet');
          }

          return isResourceAllowed;
        })
      );
    });
  };


  public isAllowedAnywhere(action: string): Signal<boolean> {
    return computed(() => {
      const policies = this.policies();
      return policies.some(
        policy => policy.statements.some(statement => {
            return statement.actions.includes(action);
          }
        )
      )
    });
  }

  constructor(private injector: Injector, private router: Router) {
    console.log('ContextService constructor');
    router.events.pipe(
      filter(event => event instanceof NavigationEnd || event instanceof NavigationSkipped),
      map(() => this.router.routerState.snapshot),
      map((rss) => [
        this.extract(rss.root, 'organization_id'),
        this.extract(rss.root, 'airline')
      ]),
      distinctUntilChanged(([prevOrg, prevAirline], [curOrg, curAirline]) =>
        prevOrg === curOrg && prevAirline === curAirline
      ),
      tap(([organizationId, airlineId]) => console.log('ContextService', organizationId, airlineId) ),
      switchMap(([organizationId, airlineId]) => {
        this.organizationId.set(organizationId);
        const apollo = this.injector.get(Apollo);

        if (organizationId === null || !organizationId.startsWith('urn:janus:organization:')) {
          this.organization.set(null);
          this.policies.set(null);
          this.licensedProducts.set(null);
          this.airlines.set(null);
          return [];
        }

        return combineLatest([
          apollo.query<RepositoryQuery>({
            query: GET_POLICIES(organizationId),
            context: {
              headers: new HttpHeaders({
                'X-Castlabs-Organization': organizationId
              })
            }
          }),
          apollo.use('workflow').query<WorkflowQuery>({
            query: GET_AIRLINES(organizationId),
            context: {
              headers: new HttpHeaders({
                'X-Castlabs-Organization': organizationId
              })
            },
            errorPolicy: 'ignore'
          })
        ]).pipe(
          filter(([policiesRes, airlinesRes]) => !!policiesRes.data?.policies && !!airlinesRes.data?.airlines),
          map(([policiesRes, airlinesRes]) => {
            this.organization.set(policiesRes.data.policies.organization);
            this.policies.set(policiesRes.data.policies.policies);
            this.licensedProducts.set(policiesRes.data.policies.licensedServices);
            const airlines = airlinesRes.data.airlines?.airlines ?? [];
            this.airlines.set([...airlines].sort((a, b) => a.airline_name.localeCompare(b.airline_name)));

            if (airlineId !== null) {
              const airline = airlinesRes.data.airlines.airlines.find(_airline => _airline.iata_code === airlineId);
              this.airline.set(airline || null);
            } else {
              this.airline.set(null);
            }
          })
        );
      })
    ).subscribe();
  }

  private extract(snapshot: ActivatedRouteSnapshot, param: string): string | null {
    // Walk the route tree to find `organization_id`
    let route: ActivatedRouteSnapshot | null = snapshot;
    while (route) {
      const orgId = route.paramMap.get(param);
      if (orgId) {
        return orgId; // Found it!
      }
      route = route.firstChild; // Move deeper in the route tree
    }
    return null; // Not found
  }


  fnMatch(pattern: string, str: string): boolean {
    // Escape special characters, then replace wildcards with RegEx equivalents
    const regexPattern = pattern
      .replace(/[.+^${}()|[\]\\]/g, '\\$&') // Escape special characters
      .replace(/\*/g, '.*') // Replace * with .*
      .replace(/\?/g, '.'); // Replace ? with .

    const regex = new RegExp(`^${regexPattern}$`);

    return regex.test(str);
  }
}
